import React, { useState, useEffect, useRef } from 'react';
import { MetaHead } from '../components/MetaHead';
import { SCRAPED_PAGES, COMPANY_INFO, COMMON_CONTACT_CALLOUT } from '../data/scrapedData';

function QualityScoreCard({ item }) {
  const [visible, setVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        } else {
          setVisible(false);
        }
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -40px 0px'
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const numericScore = parseFloat(item.score);
  const percent = (numericScore / 5.0) * 100;
  const itemColor = item.color || '#3885B8';

  return (
    <div 
      ref={cardRef} 
      className="p-5 bg-[#FAF9F6] rounded-xl border border-[#1C1C1E]/10 flex flex-col justify-between space-y-3"
    >
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center text-white shadow-xs"
            style={{ backgroundColor: itemColor }}
          >
            <i className={`fa-solid ${item.icon || 'fa-star'} text-base`} aria-hidden="true" />
          </div>
        </div>

        <h4 className="text-xs font-bold text-[#1C1C1E] leading-snug pt-1">
          {item.title}
        </h4>

        {item.description && (
          <p className="text-[11px] text-[#1C1C1E]/65 leading-tight">
            {item.description}
          </p>
        )}
      </div>

      {/* Animated Progress Bar */}
      <div className="space-y-1 pt-1">
        <div className="w-full bg-[#1C1C1E]/10 h-5 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-1000 ease-out flex items-center justify-end pr-2.5 text-xs font-bold text-white shadow-2xs"
            style={{ 
              width: visible ? `${percent}%` : '0%',
              backgroundColor: itemColor
            }}
          >
            {visible && percent > 15 && <span>{item.score}</span>}
          </div>
        </div>
        <div className="flex justify-between items-center text-[10px] text-[#1C1C1E]/50 font-semibold px-0.5">
          <span>1.0</span>
          <span>5.0</span>
        </div>
      </div>
    </div>
  );
}

export function ApprovalsAndTilsynPage() {
  const p = SCRAPED_PAGES.tilsyn;

  return (
    <>
      <MetaHead title={p.title} description={p.metaDescription} />

      <section className="bg-[#1C1C1E] text-white py-14 sm:py-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs uppercase tracking-widest font-semibold text-[#3885B8] block mb-2">
            Dokumentation & Tilsyn
          </span>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-3">
            {p.h1}
          </h1>
          <p className="text-base sm:text-lg text-white/90 max-w-3xl leading-relaxed">
            {p.intro}
          </p>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          {/* Socialtilsyn Syd Score Matrix */}
          <div className="bg-white p-6 sm:p-8 rounded-2xl border border-[#1C1C1E]/10 shadow-xs space-y-6">
            <div>
              <span className="text-xs font-bold text-[#3885B8] uppercase tracking-wider block mb-1">
                Kvalitetsmodel Score
              </span>
              <h3 className="text-xl sm:text-2xl font-bold text-[#1C1C1E]">
                Socialtilsyn Syd Vurdering
              </h3>
              <p className="text-xs sm:text-sm text-[#1C1C1E]/70 mt-1">
                Kvalitetsvurdering fra seneste tilsynsrapport (maksimal score er 5.0)
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
              {p.qualityScores.map((item) => (
                <QualityScoreCard key={item.title} item={item} />
              ))}
            </div>
          </div>

          {/* Document Cards */}
          <div className="space-y-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4 border-l-4 border-[#1C1C1E] pl-3">
              Tilsynsrapporter og Godkendelser
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {p.documents.map((doc) => (
                <div key={doc.title} className="bg-white p-6 rounded-2xl border border-[#1C1C1E]/10 shadow-xs flex flex-col justify-between space-y-4">
                  <div className="space-y-2">
                    <span className="text-[11px] font-bold text-[#3885B8] uppercase tracking-wider block">
                      {doc.category}
                    </span>
                    <h3 className="font-bold text-[#1C1C1E] text-base leading-snug">
                      {doc.title}
                    </h3>
                    <p className="text-xs text-[#1C1C1E]/60">
                      Afsender: {doc.issuer}<br />
                      Dato: {doc.date}
                    </p>
                  </div>

                  <a
                    href={doc.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center bg-[#1C1C1E] hover:bg-[#000000] text-white px-5 py-2.5 rounded-full text-xs font-semibold transition-colors w-full text-center"
                  >
                    <span>{doc.linkText}</span>
                  </a>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* Callout */}
      <section className="py-12 bg-white border-t border-[#1C1C1E]/10">
        <div className="max-w-7xl mx-auto px-4 text-center space-y-4">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">{COMMON_CONTACT_CALLOUT.header}</h2>
          <p className="text-sm text-[#1C1C1E]/80 leading-relaxed">{COMMON_CONTACT_CALLOUT.p1}</p>
          <div className="pt-2 flex justify-center gap-4">
            <a href={COMPANY_INFO.phoneTel} className="bg-[#3885B8] hover:bg-[#2A6E9B] text-white px-5 py-2.5 rounded-full font-semibold text-sm whitespace-nowrap">
              Ring {COMPANY_INFO.phone}
            </a>
            <a href={COMPANY_INFO.emailMailto} className="border border-[#3885B8] text-[#3885B8] hover:bg-[#3885B8]/10 px-5 py-2.5 rounded-full font-semibold text-sm whitespace-nowrap">
              Skriv til os
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
